import { PublicClientApplication } from "@azure/msal-browser";

// Check if running on localhost
const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Get auth config from API (if fetched) or env vars or localhost fallbacks
let apiConfig = null;
if (typeof window !== 'undefined') {
  const storedConfig = sessionStorage.getItem('__authConfigFromApi');
  if (storedConfig) {
    try {
      apiConfig = storedConfig ? JSON.parse(storedConfig) : window.__authConfigFromApi;
      console.log("Auth config loaded from sessionStorage:", apiConfig);
    } catch (error) {
      console.error("Failed to parse auth config from sessionStorage:", error);
    }
  }
}

// MSAL configuration - priority: API config > env vars > localhost fallbacks
const msalConfig = {
  auth: {
    clientId: apiConfig?.entraClientId || import.meta.env.VITE_AZURE_CLIENT_ID ||
      (isLocalhost ? "03863b5b-473c-4b05-98eb-1f81c01de8a1" : undefined),
    authority: apiConfig?.entraAuthority || import.meta.env.VITE_AZURE_AUTHORITY ||
      (isLocalhost ? "https://login.microsoftonline.com/f839cbde-26ae-4613-a48e-31fae339eda7" : undefined),
    redirectUri: window.location.origin + "/auth/callback",
    navigateToLoginRequestUrl: false,
  },

  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

// Debug logging for MSAL configuration
if (import.meta.env.DEV) {
  console.log("MSAL Configuration Debug:");
  console.log("Is Localhost:", isLocalhost);
  console.log("Client ID:", msalConfig.auth.clientId);
  console.log("Authority:", msalConfig.auth.authority);
  console.log("Redirect URI:", msalConfig.auth.redirectUri);
}

// Add scopes here for ID token to be used at Microsoft Graph API endpoints.
export const loginRequest = {
  scopes: [
    "User.Read",
    "openid",
    "profile",
    "email",
    apiConfig?.entraReadScope || import.meta.env.VITE_API_SCOPE_READ ||
    (isLocalhost ? "api://4af483b6-8028-46f0-95b3-e4bd833499fa/JWT.Read" : undefined),
    apiConfig?.entraReadWriteScope || import.meta.env.VITE_API_SCOPE_READWRITE ||
    (isLocalhost ? "api://4af483b6-8028-46f0-95b3-e4bd833499fa/JWT.ReadWrite" : undefined)
  ].filter(Boolean), // Remove any undefined values
};

// Request configuration for API access (separate from login)
export const apiRequest = {
  scopes: [
    apiConfig?.entraReadScope || import.meta.env.VITE_API_SCOPE_READ ||
    (isLocalhost ? "api://4af483b6-8028-46f0-95b3-e4bd833499fa/JWT.Read" : undefined),
    apiConfig?.entraReadWriteScope || import.meta.env.VITE_API_SCOPE_READWRITE ||
    (isLocalhost ? "api://4af483b6-8028-46f0-95b3-e4bd833499fa/JWT.ReadWrite" : undefined)
  ].filter(Boolean), // Remove any undefined values
};

// Create the main myMSALObj instance
export let msalInstance = null;

// Fetch auth config if not in sessionStorage
export const ensureAuthConfig = async () => {
  // Check if we already have config
  const storedConfig = sessionStorage.getItem('__authConfigFromApi');
  if (storedConfig) {
    console.log("Auth config found in sessionStorage");
    return JSON.parse(storedConfig);
  }
  
  // Not in sessionStorage - fetch from API
  console.log("Fetching auth config from API...");
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
    const response = await fetch(`${apiBaseUrl}/clientconfiguration`, { 
      signal: AbortSignal.timeout(5000) // 5 second timeout
    });
    if (response.ok) {
      const authConfig = await response.json();
      sessionStorage.setItem('__authConfigFromApi', JSON.stringify(authConfig));
      console.log("Auth config fetched and stored");
      return authConfig;
    }
  } catch (error) {
    console.warn("Could not fetch auth config:", error);
  }
  return null;
};
// Initialize MSAL instance
export const initializeMsal = async () => {
  try {
    console.log("Initializing MSAL instance...");
    // Ensure we have auth config
    const authConfig = await ensureAuthConfig();

    const freshMsalConfig = {
      auth: {
        clientId: authConfig?.entraClientId || import.meta.env.VITE_AZURE_CLIENT_ID ||
          (isLocalhost ? "03863b5b-473c-4b05-98eb-1f81c01de8a1" : undefined),
        authority: authConfig?.entraAuthority || import.meta.env.VITE_AZURE_AUTHORITY ||
          (isLocalhost ? "https://login.microsoftonline.com/f839cbde-26ae-4613-a48e-31fae339eda7" : undefined),
        redirectUri: window.location.origin + "/auth/callback",
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    };
    
    // Create msalInstance with fresh config
    msalInstance = new PublicClientApplication(freshMsalConfig);
    await msalInstance.initialize();
    
    console.log("MSAL instance initialized successfully");
    return msalInstance;
  } catch (error) {
    console.error("Failed to initialize MSAL:", error);
    throw error;
  }
};